import React from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@user-portal/wikr-components';

import { SUBSCRIPTION_ACTIVE } from 'constants/payments';

import { isEmpty } from 'helpers/dataStructuresUtils';

import { useDispatch } from 'hooks';
import { openModal } from 'store/modals/actions';
import { ModalName } from 'types/modals';
import { pauseFlow_pauseClick } from 'services/analytics/trackers/pauseFlow';

import { Box, Button, Text } from 'components/library';

import * as S from './styled';

import { IDefaultSubscription } from '../types';

import UpcomingProduct from './UpcomingProduct';
import SubscriptionCardWrapper from './SubscriptionCardWrapper';
import StatusIcon from './StatusIcon';
import SubscriptionCardHeader from './SubscriptionCardHeader';
import PriceRow from './PriceRow';
import { IncludedInYourPlanRow } from './IncludedInYourPlanRow';
import FeePriceRow from './FeePriceRow';
import CancelSubscriptionBlock from './CancelSubscriptionBlock';

const DefaultSubscription = ({
  feeData,
  tabIcon,
  heading,
  state,
  withFreeMonth,
  subscriptionPeriod,
  price,
  expirationDate,
  onCancelSubscription,
  upcomingProduct,
  subscriptionType,
  isUpgrade,
  isOneTimeProduct,
  canPauseSubscription,
  subscriptionId,
}: IDefaultSubscription) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const internalState = withFreeMonth ? SUBSCRIPTION_ACTIVE : state;

  const pauseSubscription = () => {
    pauseFlow_pauseClick({ subscription_id: subscriptionId.toString() });
    dispatch(openModal(ModalName.PAUSE_MODAL));
  };

  return (
    <SubscriptionCardWrapper>
      <SubscriptionCardHeader Icon={tabIcon} heading={heading} />
      <S.GeneralInfo $mb={0}>
        <S.Status>
          <StatusIcon isActive={internalState === SUBSCRIPTION_ACTIVE} />
          <Text type="caption" color="text-secondary">
            {t(`subscription.status.${internalState}`)}
          </Text>
          <Text type="caption" color="text-secondary">
            :
          </Text>
        </S.Status>
        {feeData ? (
          <FeePriceRow
            period={feeData.fee_period}
            price={feeData.fee_total_price}
            pricePerState={price}
            periodPerState={subscriptionPeriod}
          />
        ) : (
          <PriceRow
            price={price}
            period={subscriptionPeriod}
            subscriptionType={subscriptionType}
            withFreeMonth={withFreeMonth}
          />
        )}
        {isUpgrade && <IncludedInYourPlanRow />}
        <S.DateInfo data-locator="expiryDate">
          {feeData ? (
            <Text type="caption" color="text-secondary-subdued">
              {t('subscription.text.nextInstallment')}{' '}
            </Text>
          ) : (
            <Text type="caption" color="text-secondary-subdued">
              {t('subscription.text.expires')}
            </Text>
          )}
          <Text type="caption" color="text-secondary-subdued">
            {expirationDate}
          </Text>
        </S.DateInfo>
      </S.GeneralInfo>
      {upcomingProduct && !isEmpty(upcomingProduct) && !feeData && (
        <UpcomingProduct upcomingProduct={upcomingProduct} isUpgrade={isUpgrade} />
      )}
      {canPauseSubscription && (
        <Box $paddingTop={26}>
          <Text color="text-secondary" mb={10}>
            {t('subscription.pauseSubscription.heading')}
          </Text>
          <Text type="caption" color="text-secondary-subdued" mb={10}>
            {t('subscription.pauseSubscription.disclaimer')}
          </Text>
          <Button backgroundColor="action-main" onClick={pauseSubscription}>
            {t('subscription.cancellation.pauseOffer.pauseBtnText')}
          </Button>
        </Box>
      )}
      <S.Disclaimer>
        <Icon name="info" libColor="text-secondary-subdued" size={24} />
        <S.SubscriptionDisclaimer type="medium-text" color="text-secondary" mb={10}>
          {t('subscription.management.description')}
        </S.SubscriptionDisclaimer>
      </S.Disclaimer>
      <CancelSubscriptionBlock
        isOneTimeProduct={isOneTimeProduct}
        date={expirationDate}
        onCancel={onCancelSubscription}
        paddingTop={20}
      />
    </SubscriptionCardWrapper>
  );
};

export default DefaultSubscription;
